import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { View } from 'foundations-components/components/View';
import styled from 'foundations-theming/utils';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { getRootUrl } from 'pricing-pages-lib/utils/urls';
import { trellisBreakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { Redirect } from 'react-router-dom';
import { AllProductLines } from 'self-service-api/constants/ProductLines';
import { BUNDLE } from 'self-service-api/constants/UpgradeProducts';
import BannerSection from '../components/banners/BannerSection';
import PhoneSupport from '../components/common/PhoneSupport';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import HeaderLayout from '../components/layout/HeaderLayout';
import AddonsPickerList from '../components/plan-builder/AddonsPickerList';
import HubPlanPickerListContainer from '../components/plan-builder/HubPlanPickerList/HubPlanPickerListContainer';
import PlanBuilderContainer from '../components/plan-builder/PlanBuilderContainer';
import SummaryColumn from '../components/plan-builder/SummaryColumn';
import { BUNDLE_PAGE } from '../constants/ErrorBoundaryNames';
import { OWNERSHIP_CHECK_FAILED } from '../constants/ErrorCauses';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { CREATE_A_BUNDLE } from '../constants/tracking/TrackingSources';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useCountryCode, useDisplayedCurrencyCode } from '../hooks/useCurrencies';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import useIsEligibleForHsfxDiscount from '../hooks/useIsEligibleForHsfxDiscount';
import { isAppAuthSelector } from '../selectors/AuthSelectors';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../selectors/LoadingStateSelectors';
import { Metrics } from '../utils/metrics';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import ErrorPage from './ErrorPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const RightColumnWrapper = styled.div.withConfig({
  displayName: "BundlePage__RightColumnWrapper",
  componentId: "sc-5rv14-0"
})(({
  theme
}) => {
  return {
    inlineSize: '400px',
    marginBlockStart: theme.space[900],
    marginInlineStart: theme.space[300],
    [trellisBreakpoint.cs]: {
      margin: theme.space[0]
    }
  };
});
const BundlePage = () => {
  useDocumentTitle('bundle');
  const country = useCountryCode();
  const currencyCode = useDisplayedCurrencyCode();
  const eligibleForHsfxDiscount = useIsEligibleForHsfxDiscount();
  const isAppAuth = useSelector(isAppAuthSelector);
  useEffect(() => {
    getUsageTracker().setProperties({
      activeCurrencyCode: currencyCode,
      countryTerritory: country,
      defaultedTab: '',
      tab: '',
      screen: SCREENS.BUNDLE,
      trackingSource: CREATE_A_BUNDLE
    });
    trackPageView.withLastKnownEventProperties({});
    return () => {
      getUsageTracker().setProperties({
        trackingSource: undefined
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isAppReady = useGlobalLoading();
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }

  // In-App users are not allowed to access the bundle page and should be redirected to
  // the root page for recommendation engine to handle routing.
  if (isAppAuth) {
    Metrics.counter('bundle-page-redirected', {
      reason: 'in-app'
    }).increment();
    return /*#__PURE__*/_jsx(Redirect, {
      to: getRootUrl()
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
      name: "BUNDLE_PAGE"
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: 'bundle',
      children: /*#__PURE__*/_jsxs(View, {
        inlineSize: "100%",
        as: "main",
        children: [eligibleForHsfxDiscount ? /*#__PURE__*/_jsx(BannerSection, {
          screen: NavItemEnum.BUNDLE
        }) : /*#__PURE__*/_jsx(Spacer, {
          space: "400"
        }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
          children: [/*#__PURE__*/_jsx(ProductPageTitle, {
            nonProductIdentifier: NavItemEnum.BUNDLE
          }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
            nonProductIdentifier: NavItemEnum.BUNDLE
          })]
        }), /*#__PURE__*/_jsx(PlanBuilderContainer, {
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsxs(Flex, {
              flexDirection: "column",
              inlineSize: "100%",
              alignItems: "center",
              children: [/*#__PURE__*/_jsx(HubPlanPickerListContainer, {
                productLines: AllProductLines
              }), /*#__PURE__*/_jsx(AddonsPickerList, {
                defaultOpen: true
              })]
            }), /*#__PURE__*/_jsxs(RightColumnWrapper, {
              children: [/*#__PURE__*/_jsx(SummaryColumn, {
                shareLinkAnchor: "",
                hubsSignupProductOverride: "bundle"
              }), /*#__PURE__*/_jsx(PageSection, {
                children: /*#__PURE__*/_jsx(PhoneSupport, {
                  phoneSourceId: "bundle",
                  showBorder: false,
                  screen: SCREENS.BUNDLE,
                  upgradeProduct: BUNDLE
                })
              })]
            })]
          })
        })]
      })
    })]
  });
};
export default withErrorBoundary(BundlePage, {
  errorBoundaryName: BUNDLE_PAGE,
  FallbackComponent: () => /*#__PURE__*/_jsx(Redirect, {
    to: createToObject('/suite')
  })
});